import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Card,
  CardContent,
  Slider,
  Alert,
  CircularProgress,
  Avatar,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  AccountTree as StructureIcon,
  Brush as BrushIcon,
  RateReview as CriticIcon,
  Send as SendIcon,
  ContentCopy as ContentCopyIcon,
  Person as PersonIcon,
  SmartToy as BotIcon,
} from '@mui/icons-material';
import {
  ImagePromptSession,
  AgentType,
  PromptIteration
} from '../models/imagePrompt';
import { ImagePromptService } from '../services/imagePromptService';
import { useAssetRetrieval } from '../hooks/useAssetRetrieval';
import { AssetRetrievalService } from '../services/assetRetrievalService';
import ImageGenerationPanel from './ImageGenerationPanel';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`image-tabpanel-${index}`}
      aria-labelledby={`image-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ImagePromptIterator = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Fetch persisted sessions and assets from backend
  const {
    sessions: backendSessions,
    assets: backendAssets,
    loading: backendLoading,
    error: backendError,
    refetch: refetchBackendData
  } = useAssetRetrieval({
    agentType: 'ImagePromptSession',  // Fixed: Use SessionType not agent name
    sessionBased: true,
    autoFetch: true,
  });

  const [tabValue, setTabValue] = useState(0);
  const [sessions, setSessions] = useState<ImagePromptSession[]>([]);
  const [currentSession, setCurrentSession] = useState<ImagePromptSession | null>(null);
  const [newSessionDialog, setNewSessionDialog] = useState(false);
  const [newSessionTitle, setNewSessionTitle] = useState('');
  const [newSessionPrompt, setNewSessionPrompt] = useState('');
  const [selectedAgentType, setSelectedAgentType] = useState<AgentType>('Expander');
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [temperature, setTemperature] = useState(0.7);
  const [imageGenerationPrompt, setImageGenerationPrompt] = useState('');

  // Load and merge sessions from localStorage and backend
  useEffect(() => {
    // Load from localStorage
    const localSessions = ImagePromptService.loadSessions();

    // Merge with backend sessions
    if (backendSessions && backendSessions.length > 0) {
      // Convert backend sessions to ImagePromptSession format
      const convertedBackendSessions: ImagePromptSession[] = backendSessions.map(bs => {
        // Parse sessionData JSON string to extract prompt data
        let sessionData: any = {};
        try {
          if (bs.session.sessionData) {
            sessionData = JSON.parse(bs.session.sessionData);
          }
        } catch (e) {
          console.error('[ImagePromptIterator] Failed to parse sessionData:', e);
        }

        // Parse iterations from sessionData
        const iterations: PromptIteration[] = (sessionData.Iterations || []).map((iter: any) => ({
          iterationNumber: iter.IterationNumber ?? iter.iterationNumber ?? 0,
          prompt: iter.Prompt || iter.prompt || '',
          agentType: iter.AgentType || iter.agentType || 'Expander',
          timestamp: new Date(iter.Timestamp || iter.timestamp || bs.session.createdAt),
          userInstruction: iter.UserInstruction || iter.userInstruction,
          isUserAction: iter.IsUserAction ?? iter.isUserAction,
          promptVersion: iter.PromptVersion || iter.promptVersion,
        }));

        const initialPrompt = sessionData.InitialPrompt || sessionData.initialPrompt || '';

        return {
          id: bs.session.id,
          title: bs.session.title || bs.session.metadata?.title || initialPrompt || 'Untitled Session',
          initialPrompt: initialPrompt,
          currentPrompt: sessionData.CurrentPrompt || sessionData.currentPrompt || sessionData.InitialPrompt || sessionData.initialPrompt || '',
          sessionType: sessionData.SessionType || sessionData.sessionType || 'Standard',
          iterations: iterations,
          createdAt: new Date(bs.session.createdAt),
          lastUpdatedAt: new Date(bs.session.lastUpdatedAt || bs.session.updatedAt || bs.session.createdAt),
        };
      });

      // Merge: prefer backend sessions (they're authoritative)
      const sessionMap = new Map<string, ImagePromptSession>();

      // Add local sessions first
      localSessions.forEach(s => sessionMap.set(s.id, s));

      // Override with backend sessions
      convertedBackendSessions.forEach(s => sessionMap.set(s.id, s));

      setSessions(Array.from(sessionMap.values()));
    } else {
      setSessions(localSessions);
    }
  }, [backendSessions]); // Removed refetchBackendData() call

  const createNewSession = async () => {
    if (!newSessionTitle || !newSessionPrompt) return;

    setLoading(true);
    setError(null);

    try {
      // Call the API to start a new session
      const response = await ImagePromptService.startSession(newSessionPrompt);
      
      // Create a local session object from the response
      const session: ImagePromptSession = {
        id: response.sessionId,
        title: newSessionTitle,
        initialPrompt: newSessionPrompt,
        currentPrompt: response.initialPrompt || newSessionPrompt,
        sessionType: 'Standard',
        createdAt: new Date(),
        lastUpdatedAt: new Date(),
        iterations: [],
      };

      // Save to local storage and set as current
      ImagePromptService.saveSession(session);
      setCurrentSession(session);
      setNewSessionDialog(false);
      setNewSessionTitle('');
      setNewSessionPrompt('');
      setImageGenerationPrompt(session.currentPrompt);
      refetchBackendData();
      
    } catch (err) {
      setError('Failed to create session. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const selectSession = (session: ImagePromptSession) => {
    setCurrentSession(session);
    setImageGenerationPrompt(session.currentPrompt);
  };

  const deleteSession = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    ImagePromptService.deleteSession(sessionId);
    if (currentSession?.id === sessionId) {
      setCurrentSession(null);
      setImageGenerationPrompt('');
    }
    refetchBackendData();
  };

  const generateIteration = async () => {
    if (!currentSession) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Add user action if there are additional instructions
      if (additionalInstructions.trim()) {
        const userAction: PromptIteration = {
          iterationNumber: currentSession.iterations.length,
          prompt: currentSession.currentPrompt,
          agentType: selectedAgentType,
          userInstruction: additionalInstructions,
          isUserAction: true,
          timestamp: new Date(),
        };
        
        currentSession.iterations.push(userAction);
      }

      const response = await ImagePromptService.generateIteration(currentSession.id, {
        agentType: selectedAgentType,
        additionalInstructions,
        temperature,
      });

      const newIteration: PromptIteration = {
        iterationNumber: response.iterationNumber,
        prompt: response.improvedPrompt || response.processedPrompt,
        promptVersion: response.previousPrompt || response.originalPrompt,
        agentType: selectedAgentType,
        timestamp: new Date(),
      };

      currentSession.iterations.push(newIteration);
      currentSession.currentPrompt = response.improvedPrompt || response.processedPrompt;
      
      ImagePromptService.updateSession(currentSession);
      setCurrentSession({ ...currentSession });
      setImageGenerationPrompt(response.improvedPrompt || response.processedPrompt || '');
      setAdditionalInstructions('');
    } catch (err) {
      setError('Failed to generate iteration. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };


  const exportSession = () => {
    if (currentSession) {
      ImagePromptService.exportSession(currentSession);
    }
  };

  const getAgentIcon = (agentType: AgentType) => {
    switch (agentType) {
      case 'Expander':
        return <ExpandMoreIcon />;
      case 'Structurer':
        return <StructureIcon />;
      case 'Creative':
        return <BrushIcon />;
      case 'Critic':
        return <CriticIcon />;
      default:
        return <ImageIcon />;
    }
  };

  const getAgentColor = (agentType: AgentType) => {
    switch (agentType) {
      case 'Expander':
        return 'primary';
      case 'Structurer':
        return 'secondary';
      case 'Creative':
        return 'success';
      case 'Critic':
        return 'warning';
      default:
        return 'default';
    }
  };






  return (
    <Container 
      maxWidth="lg"
      sx={{ 
        px: { xs: 1, sm: 2, md: 3 },
        py: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {/* Header */}
      <Box sx={{ 
        textAlign: 'center', 
        mb: { xs: 2, sm: 3, md: 4 },
      }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 1, 
            mb: { xs: 1, sm: 2 },
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' },
          }}
        >
          <ImageIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          Image Prompt Iterator
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{
            fontSize: { xs: '0.9rem', sm: '1rem' },
            px: { xs: 1, sm: 0 },
          }}
        >
          Iteratively refine your image prompts with AI-powered agents
        </Typography>
      </Box>

      {/* Tab Navigation */}
      <Paper sx={{ mb: { xs: 2, sm: 3 } }} elevation={0}>
        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minWidth: { xs: 100, sm: 120 },
            }
          }}
        >
          <Tab label="Sessions" />
          <Tab label="Create New" />
          {currentSession && <Tab label="Current Session" />}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <TabPanel value={tabValue} index={0}>
        {/* Sessions List */}
        {sessions.length === 0 ? (
          <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              gutterBottom
              sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
            >
              No sessions found
            </Typography>
            <Button
              variant="contained"
              startIcon={!isMobile && <AddIcon />}
              onClick={() => setTabValue(1)}
              size={isMobile ? 'medium' : 'large'}
              fullWidth={isMobile}
              sx={{ mt: { xs: 1, sm: 2 } }}
            >
              Create Your First Session
            </Button>
          </Paper>
        ) : (
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
              {sessions.map((session) => (
                <Card 
                  key={session.id} 
                  variant="outlined" 
                  sx={{ cursor: 'pointer' }} 
                  onClick={() => { selectSession(session); setTabValue(2); }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between', 
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 0 },
                    }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          variant="h6"
                          sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
                        >
                          {session.title}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                        >
                          Created: {new Date(session.createdAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <IconButton
                        onClick={(e) => deleteSession(session.id, e)}
                        color="error"
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Paper>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Create New Session */}
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
            <TextField
              fullWidth
              label="Session Title"
              value={newSessionTitle}
              onChange={(e) => setNewSessionTitle(e.target.value)}
              size={isMobile ? 'small' : 'medium'}
            />
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 3 : 4}
              label="Initial Prompt"
              value={newSessionPrompt}
              onChange={(e) => setNewSessionPrompt(e.target.value)}
              size={isMobile ? 'small' : 'medium'}
            />
            <Button
              fullWidth
              size={isMobile ? 'medium' : 'large'}
              variant="contained"
              onClick={createNewSession}
              disabled={!newSessionTitle || !newSessionPrompt || loading}
              startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
            >
              {loading ? 'Creating...' : 'Create Session'}
            </Button>
            {error && (
              <Alert severity="error">
                {error}
              </Alert>
            )}
          </Box>
        </Paper>
      </TabPanel>

      {currentSession && (
        <TabPanel value={tabValue} index={2}>
          {/* Current Session */}
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
              {/* Session Header */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', sm: 'center' }, 
                mb: { xs: 1, sm: 2 },
                gap: { xs: 1, sm: 0 },
              }}>
                <Typography 
                  variant="h5"
                  sx={{
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  }}
                >
                  {currentSession.title}
                </Typography>
                <Button
                  startIcon={!isMobile && <DownloadIcon />}
                  onClick={exportSession}
                  size={isMobile ? 'small' : 'medium'}
                  fullWidth={isMobile}
                >
                  Export
                </Button>
              </Box>

              {/* Current Prompt */}
              <Box>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.125rem', sm: '1.25rem' },
                  }}
                >
                  Current Prompt
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={isMobile ? 2 : 3}
                  value={currentSession.currentPrompt}
                  size={isMobile ? 'small' : 'medium'}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <IconButton 
                        size="small" 
                        onClick={() => navigator.clipboard.writeText(currentSession.currentPrompt)}
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    )
                  }}
                />
              </Box>

              {/* Controls */}
              <Box>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.125rem', sm: '1.25rem' },
                  }}
                >
                  Generate Iteration
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
                  <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                    <InputLabel>Agent Type</InputLabel>
                    <Select
                      value={selectedAgentType}
                      onChange={(e) => setSelectedAgentType(e.target.value as AgentType)}
                      label="Agent Type"
                    >
                      <MenuItem value="Expander">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ExpandMoreIcon />
                          <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            Expander
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="Structurer">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StructureIcon />
                          <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            Structurer
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="Creative">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BrushIcon />
                          <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            Creative
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="Critic">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CriticIcon />
                          <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            Critic
                          </Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                  
                  <Box>
                    <Typography 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      Temperature: {temperature}
                    </Typography>
                    <Slider
                      value={temperature}
                      onChange={(_, value) => setTemperature(value as number)}
                      min={0}
                      max={1}
                      step={0.1}
                      marks
                      sx={{
                        '& .MuiSlider-thumb': {
                          width: { xs: 18, sm: 20 },
                          height: { xs: 18, sm: 20 },
                        },
                      }}
                    />
                  </Box>

                  <TextField
                    fullWidth
                    label="Additional Instructions"
                    value={additionalInstructions}
                    onChange={(e) => setAdditionalInstructions(e.target.value)}
                    size={isMobile ? 'small' : 'medium'}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        generateIteration();
                      }
                    }}
                  />

                  <Button
                    fullWidth
                    size={isMobile ? 'medium' : 'large'}
                    variant="contained"
                    onClick={generateIteration}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                  >
                    {loading ? 'Generating...' : 'Generate Iteration'}
                  </Button>
                </Box>
              </Box>

              {/* Image Generation Panel - Session-based with auto-persistence */}
              <ImageGenerationPanel
                initialPrompt={currentSession.currentPrompt}
                onPromptChange={(newPrompt: string) => setImageGenerationPrompt(newPrompt)}
                sessionId={currentSession.id}
                sessionType="ImagePromptSession"
              />

              {/* Iterations */}
              {currentSession.iterations.length > 0 && (
                <Box>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{
                      fontSize: { xs: '1.125rem', sm: '1.25rem' },
                    }}
                  >
                    Iteration History
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
                    {currentSession.iterations.map((iteration, index) => (
                      <Card key={index} variant="outlined">
                        <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between', 
                            alignItems: { xs: 'flex-start', sm: 'center' }, 
                            mb: { xs: 1, sm: 1 },
                            gap: { xs: 1, sm: 0 },
                          }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              flexWrap: 'wrap',
                              gap: { xs: 0.75, sm: 1 },
                            }}>
                              <Avatar sx={{ 
                                bgcolor: 'primary.main', 
                                width: { xs: 20, sm: 24 }, 
                                height: { xs: 20, sm: 24 },
                              }}>
                                {iteration.isUserAction ? 
                                  <PersonIcon sx={{ fontSize: { xs: 14, sm: 16 } }} /> : 
                                  <BotIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                                }
                              </Avatar>
                              <Chip
                                icon={getAgentIcon(iteration.agentType)}
                                label={iteration.agentType}
                                color={getAgentColor(iteration.agentType)}
                                size="small"
                                sx={{
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                  height: { xs: 20, sm: 24 },
                                }}
                              />
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                  display: { xs: 'block', sm: 'inline' },
                                  width: { xs: '100%', sm: 'auto' },
                                }}
                              >
                                Iteration {iteration.iterationNumber} • {new Date(iteration.timestamp).toLocaleString(undefined, {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Typography>
                            </Box>
                            {iteration.isUserAction && (
                              <Chip 
                                label="User Action" 
                                color="info" 
                                size="small"
                                sx={{
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                  height: { xs: 20, sm: 24 },
                                }}
                              />
                            )}
                          </Box>
                          
                          {iteration.userInstruction && (
                            <Alert 
                              severity="info" 
                              sx={{ 
                                mb: { xs: 1.5, sm: 2 },
                                py: { xs: 0.5, sm: 1 },
                              }}
                            >
                              <Typography 
                                variant="body2"
                                sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                              >
                                <strong>User instruction:</strong> {iteration.userInstruction}
                              </Typography>
                            </Alert>
                          )}
                          
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              whiteSpace: 'pre-wrap',
                              fontSize: { xs: '0.8rem', sm: '0.875rem' },
                              lineHeight: 1.5,
                            }}
                          >
                            {iteration.prompt}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </TabPanel>
      )}
    </Container>
  );
}

export default ImagePromptIterator;